import React from 'react';
import { Target, Star, BarChart, Stethoscope, Users, Clipboard } from 'lucide-react';

const Features = () => {
  const features = [
    {
      title: 'Developmental Milestones',
      description: 'Track motor skills, language, problem-solving, and social-emotional development with age-appropriate assessments.',
      icon: <Target className="w-8 h-8 text-pink-600" />,
      categories: ['0-6 months', '6-12 months', '12-24 months', '2-3 years'],
      bgColor: 'bg-gradient-to-br from-pink-50 to-pink-100'
    },
    {
      title: 'Personalized Goals',
      description: 'Set and track daily goals tailored to your child\'s development stage with guided activities and expert tips.',
      icon: <Star className="w-8 h-8 text-blue-600" />,
      categories: ['Motor Skills', 'Language', 'Social', 'Cognitive'],
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100'
    },
    {
      title: 'Progress Tracking',
      description: 'Visual timelines and achievement badges celebrate every milestone while identifying areas needing attention.',
      icon: <BarChart className="w-8 h-8 text-purple-600" />,
      categories: ['On Track', 'Advanced', 'Needs Support', 'Achieved'],
      bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100'
    },
    {
      title: 'Expert Guidance',
      description: 'Access pediatrician-approved content, weekly tips, and evidence-based activities for each development stage.',
      icon: <Stethoscope className="w-8 h-8 text-green-600" />,
      categories: ['Articles', 'Videos', 'Activities', 'Q&A'],
      bgColor: 'bg-gradient-to-br from-green-50 to-green-100'
    },
    {
      title: 'Community Support',
      description: 'Connect with parents at the same stage, share experiences, and find support in moderated circles.',
      icon: <Users className="w-8 h-8 text-orange-600" />,
      categories: ['Age Groups', 'Topics', 'Local', 'Expert-led'],
      bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100'
    },
    {
      title: 'Health Records',
      description: 'Keep immunization schedules, growth charts, and medical history organized and accessible.',
      icon: <Clipboard className="w-8 h-8 text-teal-600" />,
      categories: ['Vaccines', 'Growth', 'Visits', 'Documents'],
      bgColor: 'bg-gradient-to-br from-teal-50 to-teal-100'
    }
  ];

  return (
    <section className="py-20 bg-white" id="features">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-pink-50 rounded-full px-4 py-2 mb-4">
            <span className="text-pink-600 font-semibold text-sm uppercase tracking-wider">Features</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0C1D40] mb-6">
            Everything You Need for the First 1,000 Days
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From pregnancy to age 3, Tifl provides comprehensive tools designed by pediatricians and child development experts.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="group">
              <div className="h-full bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* Icon Container */}
                <div className={`w-16 h-16 ${feature.bgColor} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                
                {/* Content */}
                <h3 className="text-2xl font-bold text-[#0C1D40] mb-4">{feature.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                
                {/* Category Pills */}
                <div className="flex flex-wrap gap-2">
                  {feature.categories.map((category, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#F9F9F9] text-[#0C1D40] text-xs font-medium rounded-full border border-[#E6E6E6]">
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <button className="bg-[#0C1D40] text-white px-8 py-4 rounded-full hover:bg-[#1A2B50] transition-all transform hover:scale-105 font-semibold">
            Explore All Features
          </button>
        </div>
      </div>
    </section>
  );
};

export default Features;
